<template>
  <div class="h-screen flex flex-col overflow-hidden fixed inset-0">
    <!-- Compact Header -->
    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex-shrink-0 mx-5 rounded-lg shadow-sm mt-4">
      <div class="flex items-center justify-between h-16 px-5">
        <div class="flex items-center space-x-3 flex-1 min-w-0">
          <HLButton
            @click="goBack"
            variant="ghost"
            size="sm"
            class="flex-shrink-0"
            title="Back to Test Cases"
          >
            <HLIcon size="20">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </HLIcon>
          </HLButton>
          <div class="h-6 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0"></div>
          
          <!-- Test Case ID Badge -->
          <div class="bg-blue-100 dark:bg-blue-900/30 px-3 py-1 rounded border border-blue-200 dark:border-blue-700 flex-shrink-0">
            <span class="text-blue-600 dark:text-blue-400 text-sm font-semibold">
              {{ testCase?.testCaseId || testCase?.id || 'Loading...' }}
            </span>
          </div>
          
          <!-- Title -->
          <HLInput
            v-if="editingField === 'name'"
            v-model="editForm.name"
            @blur="saveEdit('name')"
            @keydown.enter="saveEdit('name')"
            @keydown.esc="cancelEdit"
            ref="nameInput"
            :error="titleError"
            class="text-lg font-semibold flex-1"
          />
          <h1 
            v-else
            @click="!testCase?.is_deleted && can.editTestCases() && startEdit('name', testCase?.name)"
            :class="!testCase?.is_deleted && can.editTestCases() ? 'cursor-pointer hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1 -mx-2 -my-1' : ''"
            class="text-lg font-semibold text-gray-900 dark:text-white truncate"
          >{{ testCase?.name || 'Loading...' }}</h1>
          
          <!-- Deleted Badge -->
          <HLBadge v-if="testCase?.is_deleted" id="testcase-deleted-badge" color="gray" size="sm" class="flex-shrink-0">Deleted</HLBadge>
            </div>
        
        <!-- Navigation Controls -->
        <div class="flex items-center space-x-3 flex-shrink-0 ml-4">
          <!-- Created Date -->
          <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <span class="mr-1.5">Created</span>
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{{ formatDateOnly(getCreatedDate) }}</span>
          </div>
          
          <!-- Delete/Restore Button -->
          <HLButton
            v-if="testCase?.is_deleted && can.deleteTestCases()"
            @click="restoreTestCase"
            variant="success"
            size="sm"
          >
            <HLIcon size="16" class="mr-1.5">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </HLIcon>
            Restore
          </HLButton>
          <HLButton
            v-else-if="!testCase?.is_deleted && can.deleteTestCases()"
            @click="deleteTestCase"
            variant="danger"
            size="sm"
          >
            <HLIcon size="16" class="mr-1.5">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </HLIcon>
            Delete
          </HLButton>
          
          <!-- Folder Navigation -->
          <div v-if="showFolderNavigation" class="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400 border-l border-r border-gray-300 dark:border-gray-600 pl-3 pr-3">
            <span>{{ folderCurrentIndex + 1 }} of {{ folderTotalTestCases }}</span>
            <HLButton
              @click="goToPreviousInFolder"
              :disabled="!hasPreviousInFolder"
              variant="ghost"
              size="sm"
            >
              <HLIcon size="16">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </HLIcon>
            </HLButton>
            <HLButton
              @click="goToNextInFolder"
              :disabled="!hasNextInFolder"
              variant="ghost"
              size="sm"
            >
              <HLIcon size="16">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </HLIcon>
            </HLButton>
        </div>

          <!-- Test Run Navigation -->
          <div v-if="showTestRunNavigation" class="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-300 dark:border-gray-600 pr-3">
            <span>{{ currentIndex + 1 }} of {{ totalTestCases }}</span>
            <HLButton
              @click="goToPrevious"
              :disabled="!hasPrevious"
              variant="ghost"
              size="sm"
            >
              <HLIcon size="16">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </HLIcon>
            </HLButton>
            <HLButton
              @click="goToNext"
              :disabled="!hasNext"
              variant="ghost"
              size="sm"
            >
              <HLIcon size="16">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </HLIcon>
            </HLButton>
    </div>

        </div>
      </div>
        </div>

    <!-- Loading State -->
    <div v-if="loading && !testCase" class="flex flex-col items-center justify-center py-12">
      <HLSpin id="testcase-detail-loading-spinner" size="lg" :show="loading && !testCase">
        <template #description>
          <HLText variant="body-sm" color="muted">Loading test case...</HLText>
        </template>
      </HLSpin>
    </div>

    <!-- Test Case Content -->
    <div v-else-if="testCase" class="flex-1 overflow-hidden px-5 mt-4">
      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full pb-4">
        <!-- Left Column - Main Info -->
        <div class="lg:col-span-2 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 scrollbar-track-transparent">
    <!-- Test Case Details -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
              <!-- Row 1: Project -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Project</span>
                <span class="text-sm text-gray-900 dark:text-white font-medium flex-1 truncate">{{ project?.name || 'N/A' }}</span>
        </div>

              <!-- Row 1: Folder -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Folder</span>
                <HLSelect
                  v-model:value="editForm.folderId"
                  :options="folderOptions"
                  @update:value="handleFolderChange"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  class="flex-1"
                />
        </div>

              <!-- Row 1: Status -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Status</span>
                <HLSelect
                  v-model:value="editForm.status"
                  :options="statusOptions"
                  @update:value="handleFieldChange('status', $event)"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <!-- Row 2: Priority -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Priority</span>
                <HLSelect
                  v-model:value="editForm.priority"
                  :options="priorityOptions"
                  @update:value="handleFieldChange('priority', $event)"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <!-- Row 2: Assignee -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Assignee</span>
                <HLSelect
                  v-model:value="editForm.assigned_to"
                  :options="assigneeOptions"
                  @update:value="handleFieldChange('assigned_to', $event)"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  filterable
                  clearable
                  class="flex-1"
                />
            </div>

              <!-- Row 2: Test Type -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-teal-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Test Type</span>
                <HLSelect
                  v-model:value="editForm.type"
                  :options="testTypeOptions"
                  @update:value="handleFieldChange('type', $event)"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <!-- Row 3: Case Type -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-pink-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Case Type</span>
                <HLSelect
                  v-model:value="editForm.case_type"
                  :options="caseTypeOptions"
                  @update:value="handleFieldChange('case_type', $event)"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <!-- Row 3: Automation Status -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Automation</span>
                <HLSelect
                  v-model:value="editForm.automation_status"
                  :options="automationStatusOptions"
                  @update:value="handleFieldChange('automation_status', $event)"
                  :disabled="!can.editTestCases()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <!-- Row 3: Estimated Time -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Est. Time</span>
                <div class="flex-1 flex items-center gap-1.5">
                  <HLInputNumber
                    v-model:value="editForm.estimated_time"
                    @update:value="handleFieldChange('estimated_time', $event)"
                    :disabled="!can.editTestCases()"
                    :min="0"
                    size="sm"
                    class="w-20"
                  />
                  <span class="text-sm text-gray-500 dark:text-gray-400">mins</span>
                </div>
          </div>
          </div>
        </div>

          <!-- Description -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 overflow-hidden">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              Description
            </h3>
            <div v-if="editingField === 'description'" class="w-full">
              <HLInput
                ref="descriptionInput"
                v-model="editForm.description"
                type="textarea"
                :rows="5"
                placeholder="Add description..."
                @blur="saveEdit('description')"
                @keydown.esc="cancelEdit"
                size="sm"
                class="w-full"
              />
            </div>
            <div 
              v-else
              @click="can.editTestCases() && startEdit('description', testCase?.description || '')"
              :class="[
                can.editTestCases() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                !testCase?.description ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
              ]"
              class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
              :style="{ 
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgb(209 213 219) transparent'
              }"
            >
              {{ testCase?.description || 'No description provided' }}
          </div>
        </div>

          <!-- Prerequisites & Test Data -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 overflow-hidden">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              Prerequisites
              </h3>
              <div v-if="editingField === 'prerequisites'" class="w-full">
                <HLInput
                  ref="prerequisitesInput"
                  v-model="editForm.prerequisites"
                  type="textarea"
                  :rows="5"
                  placeholder="Add prerequisites..."
                  @blur="saveEdit('prerequisites')"
                  @keydown.esc="cancelEdit"
                  size="sm"
                  class="w-full"
                />
              </div>
              <div 
                v-else
                @click="can.editTestCases() && startEdit('prerequisites', testCase?.prerequisites || '')"
                :class="[
                  can.editTestCases() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                  !testCase?.prerequisites ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
                ]"
                class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
                :style="{ 
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(209 213 219) transparent'
                }"
              >
              {{ testCase?.prerequisites || 'No prerequisites specified' }}
          </div>
          </div>
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 overflow-hidden">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              Test Data
              </h3>
              <div v-if="editingField === 'testData'" class="w-full">
                <HLInput
                  ref="testDataInput"
                  v-model="editForm.testData"
                  type="textarea"
                  :rows="5"
                  placeholder="Add test data..."
                  @blur="saveEdit('testData')"
                  @keydown.esc="cancelEdit"
                  size="sm"
                  class="w-full"
                />
              </div>
              <div 
                v-else
                @click="can.editTestCases() && startEdit('testData', testCase?.testData || '')"
                :class="[
                  can.editTestCases() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                  !testCase?.testData ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
                ]"
                class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
                :style="{ 
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(209 213 219) transparent'
                }"
              >
              {{ testCase?.testData || 'No test data specified' }}
              </div>
            </div>
          </div>

          <!-- Additional Info Tabs -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div class="px-6 pt-4">
              <HLTabs 
                :key="tabsKey"
                :value="activeTab" 
                @update:value="activeTab = $event"
                :animated="false" 
                size="lg"
                :maxVisibleTabs="additionalTabs.length"
              >
                <HLTab v-for="tab in additionalTabs" :key="tab.id" :name="tab.id">
                  <HLIcon size="18" class="mr-1.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tab.iconColorHex }" aria-hidden="true">
                      <path :d="tab.svgPath" />
                    </svg>
                  </HLIcon>
                  {{ tab.name }}
                  <span v-if="tab.count !== undefined" class="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    {{ tab.count }}
                  </span>
                </HLTab>
              </HLTabs>
            </div>

            <div class="p-4">
              <!-- Test Steps Tab -->
              <div v-if="activeTab === 'steps'">
                <div v-if="testSteps.length === 0 && !showAddStep && !can.editTestCases()" class="text-center py-8">
            <EmptyState
              title="No test steps"
              description="No test steps defined for this test case."
            />
          </div>
                <div v-if="testSteps.length > 0 || showAddStep || can.editTestCases()" class="overflow-x-auto">
                  <HLDataTableWrapper responsive-column-width max-width="100%">
                    <HLDataTable
                      id="test-steps-table"
                      :columns="testStepsColumns"
                      :data="testStepsTableData"
                      :loading="false"
                      row-key="id"
                      :column-hover="false"
                      :horizontal-borders="true"
                      class="test-steps-data-table"
                      :row-class="() => 'min-h-[60px]'"
                    />
                  </HLDataTableWrapper>
                  
                  <!-- Add Test Step Button Row -->
                  <div v-if="can.editTestCases() && !showAddStep" class="mt-4">
                    <HLButton
                      @click="showAddStep = true"
                      variant="outline"
                      class="w-full"
                      size="md"
                    >
                      <HLIcon size="20" class="mr-2">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </HLIcon>
                      Add Test Step
                    </HLButton>
                  </div>
          </div>
        </div>

              <!-- API Configuration Tab -->
              <div v-if="activeTab === 'api' && testCase?.type === 'api' && testCase?.apiConfig">
                <div class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg" :class="can.editTestCases() ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''" @click="can.editTestCases() && startEditApiField('method', testCase.apiConfig?.method)">
                      <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</span>
                      <div v-if="editingApiField === 'method'" class="mt-1.5">
                        <select
                          v-model="apiEditForm.method"
                          @change="saveApiField('method')"
                          class="w-full px-2 py-1 text-sm border border-primary-500 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="PATCH">PATCH</option>
                          <option value="DELETE">DELETE</option>
                        </select>
          </div>
                      <p v-else class="text-sm font-semibold text-gray-900 dark:text-white mt-1.5">{{ testCase.apiConfig.method || 'N/A' }}</p>
        </div>
                    <div class="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg" :class="can.editTestCases() ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''" @click="can.editTestCases() && startEditApiField('url', testCase.apiConfig?.url)">
                      <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">URL</span>
                      <div v-if="editingApiField === 'url'" class="mt-1.5">
                        <input
                          type="text"
                          v-model="apiEditForm.url"
                          @blur="saveApiField('url')"
                          @keydown.enter="saveApiField('url')"
                          @keydown.esc="cancelEditApiField"
                          class="w-full px-2 py-1 text-sm border border-primary-500 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
      </div>
                      <p v-else class="text-sm font-medium text-gray-900 dark:text-white mt-1.5 break-all">{{ testCase.apiConfig.url || 'N/A' }}</p>
    </div>
              </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg" :class="can.editTestCases() ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''" @click="can.editTestCases() && startEditApiField('headers', testCase.apiConfig?.headers)">
                    <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase block mb-2">Headers</span>
                    <div v-if="editingApiField === 'headers'">
                      <textarea
                        v-model="apiEditForm.headers"
                        @blur="saveApiField('headers')"
                        @keydown.esc="cancelEditApiField"
                        rows="4"
                        class="w-full px-2 py-1 text-sm border border-primary-500 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      ></textarea>
        </div>
                    <pre v-else class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">{{ testCase.apiConfig.headers || 'No headers specified' }}</pre>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg" :class="can.editTestCases() ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''" @click="can.editTestCases() && startEditApiField('body', testCase.apiConfig?.body)">
                    <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase block mb-2">Request Body</span>
                    <div v-if="editingApiField === 'body'">
                      <textarea
                        v-model="apiEditForm.body"
                        @blur="saveApiField('body')"
                        @keydown.esc="cancelEditApiField"
                        rows="4"
                        class="w-full px-2 py-1 text-sm border border-primary-500 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      ></textarea>
                    </div>
                    <pre v-else class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">{{ testCase.apiConfig.body || 'No request body specified' }}</pre>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg" :class="can.editTestCases() ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''" @click="can.editTestCases() && startEditApiField('expectedResponse', testCase.apiConfig?.expectedResponse)">
                    <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase block mb-2">Expected Response</span>
                    <div v-if="editingApiField === 'expectedResponse'">
                      <HLInput
                        v-model="apiEditForm.expectedResponse"
                        @blur="saveApiField('expectedResponse')"
                        @keydown.esc="cancelEditApiField"
                        type="textarea"
                        :rows="4"
                        size="sm"
                      />
                    </div>
                    <pre v-else class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">{{ testCase.apiConfig.expectedResponse || 'No expected response specified' }}</pre>
                  </div>
              </div>
        </div>
        
              <!-- Linked Bugs Tab -->
              <div v-if="activeTab === 'bugs'">
                <!-- Link Bug Section -->
                <div v-if="can.editTestCases()" class="mb-4 p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
                  <div class="flex items-center space-x-3">
                    <div class="flex-1 relative">
                      <HLInput
                        v-model="bugSearchQuery"
                        type="text"
                        placeholder="Search or select a bug to link..."
                        size="sm"
                        @focus="showBugDropdown = true"
                        @blur="hideBugDropdown"
                      />
                      <!-- Dropdown -->
                      <div 
                        v-if="showBugDropdown && filteredAvailableBugs.length > 0" 
                        class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      >
                        <div
                          v-for="bug in filteredAvailableBugs.slice(0, 20)"
                          :key="bug.id"
                          @mousedown.prevent="selectBugToLink(bug.id)"
                          class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                        >
                          <span class="font-medium text-blue-600 dark:text-blue-400">{{ bug.bugId }}</span>
                          <span class="text-gray-700 dark:text-gray-300"> - {{ bug.title }}</span>
              </div>
            </div>
                    </div>
                    <HLButton
                      @click="linkSelectedBug"
                      :disabled="!selectedBugId"
                      variant="primary"
                      size="sm"
                    >
                      <HLIcon size="16" class="mr-1.5">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </HLIcon>
                      Link
                    </HLButton>
                    <HLButton
                      @click="createNewBug"
                      variant="outline"
                      size="sm"
                    >
                      <HLIcon size="16" class="mr-1.5">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </HLIcon>
                      Create New
                    </HLButton>
                    <HLButton
                      @click="refreshBugs"
                      variant="outline"
                      size="sm"
                      title="Refresh bugs list"
                    >
                      <HLIcon size="16">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </HLIcon>
                    </HLButton>
          </div>
        </div>

          <div v-if="loadingLinkedBugs" class="text-center py-8 flex flex-col items-center justify-center">
            <HLSpin id="testcase-linked-bugs-spinner" size="md" :show="loadingLinkedBugs">
              <template #description>
                <HLText variant="body-sm" color="muted">Loading linked bugs...</HLText>
              </template>
            </HLSpin>
          </div>
          <div v-else-if="linkedBugs.length === 0" class="text-center py-8">
            <EmptyState
              title="No linked bugs"
              description="No bugs have been linked to this test case yet."
            />
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Bug ID</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Environment</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Priority</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Severity</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th v-if="can.editTestCases()" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="bug in linkedBugs" :key="bug.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline" @click="viewBugInNewTab(bug.id)">{{ bug.bugId || bug.id }}</td>
                        <td class="px-4 py-3 text-sm text-blue-600 dark:text-blue-400 cursor-pointer hover:underline" @click="viewBugInNewTab(bug.id)">{{ bug.title }}</td>
                        <td class="px-4 py-3 whitespace-nowrap"><HLBadge :id="`bug-env-${bug.id}`" :color="getEnvironmentVariant(bug.environment)" size="sm">{{ formatEnvironment(bug.environment) }}</HLBadge></td>
                        <td class="px-4 py-3 whitespace-nowrap"><HLBadge :id="`bug-priority-${bug.id}`" :color="getBugPriorityVariant(bug.priority)" size="sm">{{ formatBugPriority(bug.priority) }}</HLBadge></td>
                        <td class="px-4 py-3 whitespace-nowrap"><HLBadge :id="`bug-severity-${bug.id}`" :color="getBugSeverityVariant(bug.severity)" size="sm">{{ formatBugSeverity(bug.severity) }}</HLBadge></td>
                        <td class="px-4 py-3 whitespace-nowrap"><HLBadge :id="`bug-status-${bug.id}`" :color="getBugStatusVariant(bug.status)" size="sm">{{ formatBugStatus(bug.status) }}</HLBadge></td>
                        <td v-if="can.editTestCases()" class="px-4 py-3 whitespace-nowrap">
                          <HLButton
                            @click="unlinkBug(bug.id)"
                            variant="danger"
                            size="sm"
                            :disabled="isBugLinkedOnlyToThisTestCase(bug.id)"
                            :class="isBugLinkedOnlyToThisTestCase(bug.id) ? 'opacity-50 cursor-not-allowed' : ''"
                            :title="isBugLinkedOnlyToThisTestCase(bug.id) ? 'Cannot unlink bug that is linked only to this test case' : 'Unlink bug'"
                          >
                            Unlink
                          </HLButton>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

              <!-- Test Run History Tab -->
              <div v-if="activeTab === 'history'">
                <!-- Search and Filter Controls -->
                <div v-if="testRunHistory.length > 0" class="mb-4 flex items-center space-x-3">
                  <HLInput
                    v-model="testRunSearchQuery"
                    type="text"
                    placeholder="Search test runs..."
                    size="sm"
                    clearable
                    class="flex-1 min-w-0"
                    style="min-width: 557px;"
                  />
                  <HLSelect
                    v-model:value="testRunStatusFilter"
                    :options="testRunStatusOptions"
                    placeholder="All Statuses"
                    size="sm"
                    class="w-48"
                  />
                  <HLButton
                    @click="refreshTestRuns"
                    variant="outline"
                    size="sm"
                    title="Refresh test runs"
                    class="flex-shrink-0"
                  >
                    <HLIcon size="16">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </HLIcon>
                  </HLButton>
                </div>

          <div v-if="loadingTestRuns" class="text-center py-8 flex flex-col items-center justify-center">
            <HLSpin id="testcase-testruns-spinner" size="md" :show="loadingTestRuns">
              <template #description>
                <HLText variant="body-sm" color="muted">Loading test run history...</HLText>
              </template>
            </HLSpin>
          </div>
          <div v-else-if="testRunHistory.length === 0" class="text-center py-8">
            <EmptyState
              title="No test run history"
              description="This test case has not been executed in any test runs yet."
            />
          </div>
                <div v-else-if="filteredTestRunHistory.length === 0" class="text-center py-8">
                  <p class="text-gray-500 dark:text-gray-400">No test runs match your search criteria.</p>
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Test Run</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Executed By</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Executed At</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr v-for="run in filteredTestRunHistory" :key="run.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-4 py-3 whitespace-nowrap cursor-pointer" @click="viewTestRunInNewTab(run.id)">
                          <span class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">{{ run.testRunName }}</span>
                          <div class="text-xs text-gray-500 dark:text-gray-400">{{ run.testRunId }}</div>
                  </td>
                        <td class="px-4 py-3 whitespace-nowrap"><HLBadge :id="`execution-status-${run.id}`" :color="getExecutionStatusVariant(run.status)" size="sm">{{ formatExecutionStatus(run.status) }}</HLBadge></td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ run.executedBy || 'Not executed' }}</td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ formatDate(run.executedAt) }}</td>
                </tr>
              </tbody>
            </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Activity Sidebar -->
        <div class="h-full overflow-hidden flex flex-col">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0">
            <div class="px-6 pt-4">
              <HLTabs 
                :key="`sidebar-tabs-${activityCount}`"
                :value="activeSidebarTab" 
                @update:value="activeSidebarTab = $event"
                :animated="false" 
                size="lg"
                :maxVisibleTabs="sidebarTabs.length"
              >
                <HLTab v-for="tab in sidebarTabs" :key="`${tab.id}-${tab.count}`" :name="tab.id">
                  <HLIcon size="18" class="mr-1.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tab.iconColorHex }" aria-hidden="true">
                      <path :d="tab.svgPath" />
                    </svg>
                  </HLIcon>
                  {{ tab.name }}
                  <span v-if="tab.count !== undefined" class="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    {{ tab.count }}
                  </span>
                </HLTab>
              </HLTabs>
            </div>

            <div class="p-4 flex-1 flex flex-col min-h-0">
              <!-- Activity Tab -->
              <div v-if="activeSidebarTab === 'activity'" class="flex flex-col h-full min-h-0">
                <div v-if="loadingActivity" class="text-center py-8 flex flex-col items-center justify-center">
                  <HLSpin id="testcase-activity-spinner" size="md" :show="loadingActivity">
                    <template #description>
                      <HLText variant="body-sm" color="muted">Loading activity...</HLText>
                    </template>
                  </HLSpin>
                </div>
                <div v-else-if="activities.length === 0" class="text-gray-500 dark:text-gray-400 text-center py-4 flex-1 flex items-center justify-center">
                  <div class="text-center">
                    <svg class="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p class="text-sm">No activity yet</p>
                  </div>
                </div>
                <div v-else class="space-y-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 scrollbar-track-transparent pr-2 pl-4 min-h-0">
                <div
                  v-for="(activity, index) in activities"
                  :key="index"
                  class="relative pl-8 pb-2 ml-1 border-l-2 border-gray-200 dark:border-gray-700 last:border-transparent"
                >
                  <!-- Activity Icon -->
                  <div class="absolute -left-4 top-0">
                    <div class="w-7 h-7 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800"
                      :class="getActivityIconClass(activity.type)"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path v-if="activity.type === 'created'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        <path v-else-if="activity.type === 'deleted'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        <path v-else-if="activity.type === 'restored'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        <path v-else-if="activity.type === 'status_changed'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path v-else-if="activity.type === 'executed'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path v-else-if="activity.type === 'bug_linked'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
        </div>
                </div>
                  
                  <!-- Activity Content -->
                  <div class="ml-2">
                    <div class="flex items-start justify-between gap-2">
                      <p class="text-sm text-gray-900 dark:text-white flex-1">
                        <span class="font-semibold">{{ activity.user_name || 'Unknown' }}</span> <span v-if="activity.description" class="text-gray-600 dark:text-gray-400">{{ formatActivityDescription(activity) }}</span><span v-else class="text-gray-600 dark:text-gray-400"> {{ activity.title }}</span>
                      </p>
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ formatDate(activity.timestamp) }}</p>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <p class="text-gray-500 dark:text-gray-400">Test case not found</p>
        <button @click="goBack" class="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
          Go Back
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, h } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTestCaseStore } from '@/stores/testCase'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { useTestRunStore } from '@/stores/testRun'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { useToast } from '@/composables/useToast'
import { testCaseService, bugService } from '@/database/services'
import { HLButton, HLBadge, HLTag, HLSpace, HLTabs, HLTab, HLIcon, HLInput, HLSelect, HLInputNumber, HLDataTable, HLDataTableWrapper, HLSpin, HLText } from '@platform-ui/highrise'
import EmptyState from '@/components/ui/EmptyState.vue'
import {
  getTestCaseTypeByValue,
  getCaseTypeByValue,
  getAutomationStatusByValue,
  getTestCaseStatusByValue,
  getPriorityByValue,
  TEST_CASE_TYPES,
  CASE_TYPES,
  AUTOMATION_STATUS,
  TEST_CASE_STATUS,
  PRIORITY_LEVELS
} from '@/constants/testCaseConstants'
import { getIconPath } from '@/constants/icons'
import { TAB_ICON_COLORS } from '@/constants/projects'

const route = useRoute()
const router = useRouter()
const testCaseStore = useTestCaseStore()
const projectStore = useProjectStore()
const userStore = useUserStore()
const testRunStore = useTestRunStore()
const authStore = useAuthStore()
const { can } = usePermissions()
const { success: showSuccess, error: showError, info: showInfo } = useToast()

const loading = ref(false)
const loadingTestRuns = ref(false)
const loadingActivity = ref(false)
const loadingLinkedBugs = ref(false)
const testCase = ref(null)
const projects = ref([])
const users = ref([])
const folders = ref([])
const testRunHistory = ref([])
const activities = ref([])
const linkedBugs = ref([])
const activeTab = ref('steps')
const activeSidebarTab = ref('activity')
const availableBugs = ref([])
const selectedBugId = ref(null)
const bugSearchQuery = ref('')
const showBugDropdown = ref(false)
const testRunSearchQuery = ref('')
const testRunStatusFilter = ref('')
const tabsKey = ref(0)

const testRunStatusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'retest', label: 'Retest' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'skipped', label: 'Skipped' }
]

// Inline editing state
const editingField = ref(null)
const editForm = ref({
  name: '',
  folderId: '',
  status: '',
  priority: '',
  assigned_to: '',
  type: '',
  case_type: '',
  automation_status: '',
  estimated_time: '',
  description: '',
  prerequisites: '',
  testData: ''
})
const nameInput = ref(null)
const titleError = ref(false)
const descriptionInput = ref(null)
const prerequisitesInput = ref(null)
const testDataInput = ref(null)

// Test steps editing state
const editingStepIndex = ref(null)
const editStepForm = ref({ description: '', expectedResult: '' })
const newStepForm = ref({ description: '', expectedResult: '' })
const showAddStep = ref(false)

// API config editing state
const editingApiField = ref(null)
const apiEditForm = ref({
  method: '',
  url: '',
  headers: '',
  body: '',
  expectedResponse: ''
})

// Navigation data
const testRunId = ref(null)
const testRunTestCases = ref([])
const currentTestCaseId = ref(null)

// Folder navigation data
const folderTestCases = ref([])
const folderCurrentTestCaseId = ref(null)

const testSteps = computed(() => {
  if (!testCase.value?.steps) return []
  if (Array.isArray(testCase.value.steps)) {
    return testCase.value.steps
  }
  if (typeof testCase.value.steps === 'string') {
    return testCase.value.steps.split('\n').filter(step => step.trim().length > 0)
  }
  return []
})

// Table data with indices for HLDataTable
const testStepsTableData = computed(() => {
  const steps = testSteps.value.map((step, index) => ({
    id: index,
    index: index + 1,
    step,
    description: step.description || step.action || (typeof step === 'string' ? step : ''),
    expectedResult: step.expectedResult || ''
  }))
  
  // Add "add new" row if needed
  if (can.editTestCases() && showAddStep.value) {
    steps.push({
      id: 'new',
      index: steps.length + 1,
      step: null,
      description: '',
      expectedResult: '',
      isNew: true
    })
  }
  
  return steps
})

// HLDataTable columns configuration
const testStepsColumns = computed(() => {
  const columns = [
    {
      id: 'step',
      accessorKey: 'index',
      header: 'Step',
      size: 80,
      enableResizing: false,
      meta: { align: 'center', headerAlign: 'center' },
      cellFormatter: (props) => {
        const row = props.row.original
        const isNew = row.isNew
        const isEditing = editingStepIndex.value === row.id
        
        if (isNew) {
          return h('div', { 
            class: 'inline-flex items-center justify-center w-7 h-7 bg-blue-100 dark:bg-blue-900/30 rounded-full flex-shrink-0',
            style: { minWidth: '28px', minHeight: '28px' }
          }, [
            h('span', { class: 'text-xs font-semibold text-blue-700 dark:text-blue-300 leading-none' }, String(row.index))
          ])
        }
        
        if (isEditing) {
          return h('div', { 
            class: 'inline-flex items-center justify-center w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded-full flex-shrink-0',
            style: { minWidth: '28px', minHeight: '28px' }
          }, [
            h('span', { class: 'text-xs font-semibold text-gray-700 dark:text-gray-300 leading-none' }, String(row.index))
          ])
        }
        
        return h('div', { 
          class: 'inline-flex items-center justify-center w-7 h-7 bg-blue-100 dark:bg-blue-900/30 rounded-full flex-shrink-0',
          style: { minWidth: '28px', minHeight: '28px' }
        }, [
          h('span', { class: 'text-xs font-semibold text-blue-700 dark:text-blue-300 leading-none' }, String(row.index))
        ])
      }
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Test Step',
      size: 300,
      enableResizing: true,
      meta: { align: 'left' },
      cellFormatter: (props) => {
        const row = props.row.original
        const isEditing = editingStepIndex.value === row.id
        const isNew = row.isNew
        
        if (isNew) {
          return h('div', { class: 'w-full max-w-full' }, [
            h(HLInput, {
              modelValue: newStepForm.value.description,
              'onUpdate:modelValue': (val) => { newStepForm.value.description = val },
              type: 'textarea',
              rows: 2,
              placeholder: 'Enter test step description...',
              size: 'sm',
              class: 'w-full max-w-full'
            })
          ])
        }
        
        if (isEditing) {
          return h('div', { class: 'w-full max-w-full' }, [
            h(HLInput, {
              modelValue: editStepForm.value.description,
              'onUpdate:modelValue': (val) => { editStepForm.value.description = val },
              type: 'textarea',
              rows: 2,
              size: 'sm',
              class: 'w-full max-w-full'
            })
          ])
        }
        
        return h('div', { class: 'text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words' }, 
          row.description || 'No description'
        )
      }
    },
    {
      id: 'expectedResult',
      accessorKey: 'expectedResult',
      header: 'Expected Result',
      size: 300,
      enableResizing: true,
      meta: { align: 'left' },
      cellFormatter: (props) => {
        const row = props.row.original
        const isEditing = editingStepIndex.value === row.id
        const isNew = row.isNew
        
        if (isNew) {
          return h('div', { class: 'w-full max-w-full' }, [
            h(HLInput, {
              modelValue: newStepForm.value.expectedResult,
              'onUpdate:modelValue': (val) => { newStepForm.value.expectedResult = val },
              type: 'textarea',
              rows: 2,
              placeholder: 'Enter expected result...',
              size: 'sm',
              class: 'w-full max-w-full'
            })
          ])
        }
        
        if (isEditing) {
          return h('div', { class: 'w-full max-w-full' }, [
            h(HLInput, {
              modelValue: editStepForm.value.expectedResult,
              'onUpdate:modelValue': (val) => { editStepForm.value.expectedResult = val },
              type: 'textarea',
              rows: 2,
              size: 'sm',
              class: 'w-full max-w-full'
            })
          ])
        }
        
        return h('div', { class: 'text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words' }, 
          row.expectedResult || 'No expected result specified'
        )
      }
    }
  ]
  
  // Add Actions column only if user can edit
  if (can.editTestCases()) {
    columns.push({
      id: 'actions',
      accessorKey: 'actions',
      header: 'Actions',
      size: 200,
      minSize: 180,
      enableResizing: false,
      meta: { align: 'center', headerAlign: 'center' },
      cellFormatter: (props) => {
        const row = props.row.original
        const isEditing = editingStepIndex.value === row.id
        const isNew = row.isNew
        
        if (isNew) {
          return h('div', { class: 'flex items-center justify-center gap-1' }, [
            h(HLButton, {
              onClick: () => addStep(),
              variant: 'primary',
              size: 'sm',
              title: 'Add Step'
            }, {
              default: () => h(HLIcon, { size: 16 }, {
                default: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
                  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M5 13l4 4L19 7' })
                ])
              })
            }),
            h(HLButton, {
              onClick: () => cancelAddStep(),
              variant: 'ghost',
              size: 'sm',
              title: 'Cancel'
            }, {
              default: () => h(HLIcon, { size: 16 }, {
                default: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
                  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M6 18L18 6M6 6l12 12' })
                ])
              })
            })
          ])
        }
        
        if (isEditing) {
          return h('div', { class: 'flex items-center justify-center gap-1' }, [
            h(HLButton, {
              onClick: () => saveStep(row.id),
              variant: 'success',
              size: 'sm',
              title: 'Save'
            }, {
              default: () => h(HLIcon, { size: 16 }, {
                default: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
                  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M5 13l4 4L19 7' })
                ])
              })
            }),
            h(HLButton, {
              onClick: () => cancelEditStep(),
              variant: 'ghost',
              size: 'sm',
              title: 'Cancel'
            }, {
              default: () => h(HLIcon, { size: 16 }, {
                default: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
                  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M6 18L18 6M6 6l12 12' })
                ])
              })
            })
          ])
        }
        
        return h('div', { class: 'flex items-center justify-center gap-1' }, [
          h(HLButton, {
            onClick: () => startEditStep(row.id, row.step),
            variant: 'ghost',
            size: 'sm',
            title: 'Edit'
          }, {
            default: () => h(HLIcon, { size: 16 }, {
              default: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
                h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' })
              ])
            })
          }),
          h(HLButton, {
            onClick: () => deleteStep(row.id),
            variant: 'danger',
            size: 'sm',
            title: 'Delete'
          }, {
            default: () => h(HLIcon, { size: 16 }, {
              default: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
                h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' })
              ])
            })
          })
        ])
      }
    })
  }
  
  return columns
})

const project = computed(() => {
  const projectId = route.params.projectId
  return projects.value.find(p => p.id === projectId) || null
})

const filteredAvailableBugs = computed(() => {
  if (!bugSearchQuery.value.trim()) return availableBugs.value
  
  const query = bugSearchQuery.value.toLowerCase().trim()
  return availableBugs.value.filter(bug => {
    const bugId = (bug.bugId || bug.id || '').toLowerCase()
    const title = (bug.title || '').toLowerCase()
    const status = (bug.status || '').toLowerCase()
    
    return bugId.includes(query) || title.includes(query) || status.includes(query)
  })
})


const filteredTestRunHistory = computed(() => {
  let filtered = [...testRunHistory.value]
  
  // Apply status filter
  if (testRunStatusFilter.value && testRunStatusFilter.value !== '') {
    filtered = filtered.filter(run => {
      // Normalize status values for comparison (handle both string and potential variations)
      const runStatus = (run.status || '').toLowerCase().trim()
      const filterStatus = (testRunStatusFilter.value || '').toLowerCase().trim()
      return runStatus === filterStatus
    })
  }
  
  // Apply search query
  if (testRunSearchQuery.value.trim()) {
    const query = testRunSearchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(run => {
      const testRunName = (run.testRunName || '').toLowerCase()
      const testRunId = (run.testRunId || '').toLowerCase()
      const executedBy = (run.executedBy || '').toLowerCase()
      const status = (run.status || '').toLowerCase()
      
      return testRunName.includes(query) || testRunId.includes(query) || 
             executedBy.includes(query) || status.includes(query)
    })
  }
  
  return filtered
})

const additionalTabs = computed(() => {
  const tabs = [
    { 
      id: 'steps', 
      name: 'Test Steps', 
      count: testSteps.value.length,
      svgPath: getIconPath('documentText'),
      iconColorHex: TAB_ICON_COLORS.TEST_CASES
    }
  ]
  
  if (testCase.value?.type === 'api' && testCase.value?.apiConfig) {
    tabs.push({ 
      id: 'api', 
      name: 'API Config',
      svgPath: getIconPath('settings'),
      iconColorHex: TAB_ICON_COLORS.DEFAULT
    })
  }
  
  tabs.push(
    { 
      id: 'bugs', 
      name: 'Linked Bugs', 
      count: linkedBugs.value.length,
      svgPath: getIconPath('bug'),
      iconColorHex: TAB_ICON_COLORS.BUGS
    },
    { 
      id: 'history', 
      name: 'Test Run History', 
      count: testRunHistory.value.length,
      svgPath: getIconPath('clock'),
      iconColorHex: TAB_ICON_COLORS.ACTIVITY
    }
  )
  
  return tabs
})

// Computed property for activity count
const activityCount = computed(() => {
  return activities.value?.length ?? 0
})

// Computed property for sidebar tabs
const sidebarTabs = computed(() => {
  return [
  { 
    id: 'activity', 
    name: 'Activity', 
      count: activityCount.value,
    svgPath: getIconPath('clock'),
    iconColorHex: TAB_ICON_COLORS.ACTIVITY
  }
  ]
})

// Options for HLSelect components
const folderOptions = computed(() => {
  const options = [{ value: '', label: 'Root' }]
  if (folderList.value && folderList.value.length > 0) {
    folderList.value.forEach(folder => {
      options.push({ value: folder.id, label: folder.displayName })
    })
  }
  
  // If test case has a folderId that's not in the options, add it as a fallback
  // This handles cases where folders haven't loaded yet or folder was deleted
  // Only add if folderId is not empty (empty means "Root" which is already in options)
  const currentFolderId = testCase.value?.folderId
  if (currentFolderId && String(currentFolderId).trim() !== '' && !options.find(opt => opt.value === currentFolderId)) {
    // Try to get folder name from folderList first (more reliable)
    const folderInList = folderList.value?.find(f => f.id === currentFolderId)
    const folderName = folderInList?.name || folderInList?.displayName || getFolderName(currentFolderId)
    
    // Only add fallback if we have a name, otherwise it will show the ID
    if (folderName) {
      options.push({ 
        value: currentFolderId, 
        label: folderName
      })
    } else {
      // If we can't find the folder name, don't add it to options
      // This will cause HLSelect to show the value, but we'll handle it by normalizing to ''
      // The issue is that the folder might have been deleted
    }
  }
  
  return options
})

const statusOptions = computed(() => {
  return TEST_CASE_STATUS.map(option => ({
    value: option.value,
    label: option.label
  }))
})

const priorityOptions = computed(() => {
  return PRIORITY_LEVELS.map(option => ({
    value: option.value,
    label: option.label
  }))
})

const assigneeOptions = computed(() => {
  const options = [{ value: '', label: 'Unassigned' }]
  if (users.value && users.value.length > 0) {
    users.value.forEach(user => {
      options.push({ value: user.id, label: user.name || user.email || 'Unknown' })
    })
  }
  return options
})

const testTypeOptions = computed(() => {
  return TEST_CASE_TYPES.map(option => ({
    value: option.value,
    label: option.label
  }))
})

const caseTypeOptions = computed(() => {
  return CASE_TYPES.map(option => ({
    value: option.value,
    label: option.label
  }))
})

const automationStatusOptions = computed(() => {
  return AUTOMATION_STATUS.map(option => ({
    value: option.value,
    label: option.label
  }))
})

// Navigation computed properties
const showFolderNavigation = computed(() => {
  return folderTestCases.value.length > 1
})

const showTestRunNavigation = computed(() => {
  return testRunId.value && testRunTestCases.value.length > 1
})

const currentIndex = computed(() => {
  if (!testRunTestCases.value.length) return 0
  // Try to find by database id first (route uses id)
  const indexById = testRunTestCases.value.findIndex(tc => tc.id === route.params.caseId)
  if (indexById !== -1) return indexById
  
  // Fallback to testCaseId
  if (currentTestCaseId.value) {
  const index = testRunTestCases.value.findIndex(tc => tc.testCaseId === currentTestCaseId.value)
    if (index !== -1) return index
  }
  
  // Final fallback
    const fallbackIndex = testRunTestCases.value.findIndex(tc => tc.testCaseId === route.params.caseId)
    return fallbackIndex === -1 ? 0 : fallbackIndex
})

const totalTestCases = computed(() => testRunTestCases.value.length)
const hasPrevious = computed(() => currentIndex.value > 0)
const hasNext = computed(() => currentIndex.value < totalTestCases.value - 1)

const folderCurrentIndex = computed(() => {
  if (!folderTestCases.value.length) return 0
  // Try to find by database id first (route uses id)
  const indexById = folderTestCases.value.findIndex(tc => tc.id === route.params.caseId)
  if (indexById !== -1) return indexById
  
  // Fallback to testCaseId
  if (folderCurrentTestCaseId.value) {
  const index = folderTestCases.value.findIndex(tc => tc.testCaseId === folderCurrentTestCaseId.value)
    if (index !== -1) return index
  }
  
  // Final fallback
    const fallbackIndex = folderTestCases.value.findIndex(tc => tc.testCaseId === route.params.caseId)
    return fallbackIndex === -1 ? 0 : fallbackIndex
})

const folderTotalTestCases = computed(() => folderTestCases.value.length)
const hasPreviousInFolder = computed(() => folderCurrentIndex.value > 0)
const hasNextInFolder = computed(() => folderCurrentIndex.value < folderTotalTestCases.value - 1)

const getPriorityVariant = (priority) => {
  const variants = {
    'LOW': 'secondary', 'MEDIUM': 'info', 'HIGH': 'warning', 'CRITICAL': 'danger',
    'low': 'secondary', 'medium': 'info', 'high': 'warning', 'critical': 'danger'
  }
  return variants[priority] || 'secondary'
}

const getStatusVariant = (status) => {
  const variants = {
    'DRAFT': 'secondary', 'ACTIVE': 'success', 'INACTIVE': 'warning', 'ARCHIVED': 'danger',
    'draft': 'secondary', 'active': 'success', 'inactive': 'warning', 'archived': 'danger'
  }
  return variants[status] || 'secondary'
}

const getExecutionStatusVariant = (status) => {
  const variants = {
    'not_started': 'secondary', 'in_progress': 'info', 'passed': 'success',
    'failed': 'danger', 'retest': 'warning', 'blocked': 'warning', 'skipped': 'secondary'
  }
  return variants[status] || 'secondary'
}

// Format activity description, resolving user IDs to names for assignee changes and folder IDs to names
const formatActivityDescription = (activity) => {
  if (!activity.description) return activity.description
  
  // Check if this is an assigned_to field change
  if (activity.type === 'field_updated' && activity.details?.field === 'assigned_to') {
    // Extract old and new values from description or details
    const oldValue = activity.details?.oldValue
    const newValue = activity.details?.newValue
    
    // Helper to get user name from ID
    const getUserName = (userId) => {
      if (!userId || userId === '' || userId === 'Not set') return 'Unassigned'
      const user = users.value.find(u => u.id === userId || u.uid === userId)
      return user ? (user.name || user.email || 'Unknown User') : userId
    }
    
    const oldName = getUserName(oldValue)
    const newName = getUserName(newValue)
    
    return `Assigned To changed from "${oldName}" to "${newName}"`
  }
  
  // Check if this is a folderId field change
  if (activity.type === 'field_updated' && activity.details?.field === 'folderId') {
    // Extract old and new values from description or details
    const oldValue = activity.details?.oldValue
    const newValue = activity.details?.newValue
    
    // Helper to get folder name from ID
    const getFolderName = (folderId) => {
      if (!folderId || folderId === '' || folderId === 'Not set') return 'Root'
      // Check if it's already a name (doesn't look like a folder ID)
      if (!folderId.startsWith('folder_')) {
        return folderId // Already a name
      }
      const folder = folderList.value?.find(f => f.id === folderId)
      return folder ? (folder.name || folder.displayName || 'Unknown Folder') : folderId
    }
    
    const oldName = getFolderName(oldValue)
    const newName = getFolderName(newValue)
    
    return `Folder Id changed from "${oldName}" to "${newName}"`
  }
  
  // For other fields, check if description contains user IDs that look like assignee IDs
  // Pattern: "Assigned To changed from "user_xxx" to "user_yyy""
  const assigneePattern = /Assigned To changed from "([^"]+)" to "([^"]+)"/
  const match = activity.description.match(assigneePattern)
  if (match) {
    const oldUserId = match[1]
    const newUserId = match[2]
    
    // Helper to get user name from ID
    const getUserName = (userId) => {
      if (!userId || userId === '' || userId === 'Not set') return 'Unassigned'
      // Check if it's already a name (doesn't start with 'user_' or look like an ID)
      if (!userId.startsWith('user_') && !userId.match(/^[a-z0-9_]+$/i)) {
        return userId // Already a name
      }
      const user = users.value.find(u => u.id === userId || u.uid === userId)
      return user ? (user.name || user.email || 'Unknown User') : userId
    }
    
    const oldName = getUserName(oldUserId)
    const newName = getUserName(newUserId)
    
    return `Assigned To changed from "${oldName}" to "${newName}"`
  }
  
  // Check if description contains folderId changes with folder IDs
  // Pattern: "Folder Id changed from "folder_xxx" to "folder_yyy"" or "FolderId changed from..."
  const folderPattern = /Folder\s*Id\s+changed\s+from\s+"([^"]+)"\s+to\s+"([^"]+)"/
  const folderMatch = activity.description.match(folderPattern)
  if (folderMatch) {
    const oldFolderId = folderMatch[1]
    const newFolderId = folderMatch[2]
    
    // Helper to get folder name from ID
    const getFolderName = (folderId) => {
      if (!folderId || folderId === '' || folderId === 'Not set') return 'Root'
      // Check if it's already a name (doesn't look like a folder ID)
      if (!folderId.startsWith('folder_')) {
        return folderId // Already a name
      }
      const folder = folderList.value?.find(f => f.id === folderId)
      return folder ? (folder.name || folder.displayName || 'Unknown Folder') : folderId
    }
    
    const oldName = getFolderName(oldFolderId)
    const newName = getFolderName(newFolderId)
    
    return `Folder Id changed from "${oldName}" to "${newName}"`
  }
  
  // Return original description for other cases
  return activity.description
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch (error) {
    return 'Invalid Date'
  }
}

const formatDateOnly = (date) => {
  if (!date) return 'N/A'
  try {
  return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
  })
  } catch (error) {
    return 'Invalid Date'
  }
}

// Get created date from test case or activities
const getCreatedDate = computed(() => {
  // First try to get from testCase.created_at or createdAt
  if (testCase.value?.created_at) {
    return testCase.value.created_at
  }
  if (testCase.value?.createdAt) {
    return testCase.value.createdAt
  }
  
  // Fallback: try to get from the first "created" activity
  if (activities.value && activities.value.length > 0) {
    const createdActivity = activities.value.find(a => a.type === 'created')
    if (createdActivity && createdActivity.timestamp) {
      return createdActivity.timestamp
    }
    // If no created activity, use the oldest activity timestamp
    const sortedActivities = [...activities.value].sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return dateA - dateB
    })
    if (sortedActivities.length > 0 && sortedActivities[0].timestamp) {
      return sortedActivities[0].timestamp
    }
  }
  
  return null
})

const deleteTestCase = async () => {
  if (!confirm('Are you sure you want to delete this test case?')) return
  
  try {
    const userId = authStore.user?.uid || authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    await testCaseStore.deleteTestCase(route.params.caseId, userId, userName)
    showSuccess('Test case deleted successfully')
    router.push(`/projects/${route.params.projectId}/test-cases`)
  } catch (error) {
    console.error('Delete test case error:', error)
    showError('Failed to delete test case')
  }
}

const restoreTestCase = async () => {
  if (!confirm('Are you sure you want to restore this test case?')) return
  
  try {
    const userId = authStore.user?.uid || authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    await testCaseStore.restoreTestCase(route.params.caseId, userId, userName)
    showSuccess('Test case restored successfully')
    await loadTestCase()
  } catch (error) {
    console.error('Restore test case error:', error)
    showError('Failed to restore test case')
  }
}

// Navigation functions
const goToPrevious = () => {
  if (!hasPrevious.value) return
  const prevTestCase = testRunTestCases.value[currentIndex.value - 1]
  if (prevTestCase) {
    // Use database id if available, otherwise fallback to testCaseId
    const caseId = prevTestCase.id || prevTestCase.testCaseId
    router.push(`/projects/${route.params.projectId}/test-cases/${caseId}${route.query.testRunId ? `?testRunId=${route.query.testRunId}` : ''}`)
  }
}

const goToNext = () => {
  if (!hasNext.value) return
  const nextTestCase = testRunTestCases.value[currentIndex.value + 1]
  if (nextTestCase) {
    // Use database id if available, otherwise fallback to testCaseId
    const caseId = nextTestCase.id || nextTestCase.testCaseId
    router.push(`/projects/${route.params.projectId}/test-cases/${caseId}${route.query.testRunId ? `?testRunId=${route.query.testRunId}` : ''}`)
  }
}

const goToPreviousInFolder = () => {
  if (!hasPreviousInFolder.value) return
  const prevTestCase = folderTestCases.value[folderCurrentIndex.value - 1]
  if (prevTestCase) {
    // Use database id if available, otherwise fallback to testCaseId
    const caseId = prevTestCase.id || prevTestCase.testCaseId
    router.push(`/projects/${route.params.projectId}/test-cases/${caseId}${route.query.testRunId ? `?testRunId=${route.query.testRunId}` : ''}`)
  }
}

const goToNextInFolder = () => {
  if (!hasNextInFolder.value) return
  const nextTestCase = folderTestCases.value[folderCurrentIndex.value + 1]
  if (nextTestCase) {
    // Use database id if available, otherwise fallback to testCaseId
    const caseId = nextTestCase.id || nextTestCase.testCaseId
    router.push(`/projects/${route.params.projectId}/test-cases/${caseId}${route.query.testRunId ? `?testRunId=${route.query.testRunId}` : ''}`)
  }
}

const loadTestCase = async () => {
  try {
    loading.value = true
    const caseId = route.params.caseId
    
    // Fetch test case by ID directly (includes deleted test cases)
    const response = await testCaseStore.fetchTestCaseById(caseId)
    
    if (!response) throw new Error('Test case not found')
    
    testCase.value = response
    currentTestCaseId.value = response.testCaseId || response.id
    
    // Normalize folderId: if it's null, undefined, empty string, or whitespace, set to '' (Root)
    let normalizedFolderId = ''
    if (response.folderId) {
      const trimmed = String(response.folderId).trim()
      normalizedFolderId = trimmed !== '' ? trimmed : ''
    }
    
    // Sync editForm with testCase values for HLSelect components
    // Note: folderId will be set after folders are loaded and validated
    editForm.value = {
      name: response.name || '',
      folderId: '', // Will be set after folders are loaded and validated
      status: response.status || '',
      priority: response.priority || '',
      assigned_to: response.assigned_to || '',
      type: response.type || '',
      case_type: response.case_type || '',
      automation_status: response.automation_status || '',
      estimated_time: response.estimated_time || '',
      description: response.description || '',
      prerequisites: response.prerequisites || '',
      testData: response.testData || ''
    }
    
    // Load folders first to ensure folderOptions is populated before setting editForm.folderId
    await loadFolders()
    
    // After folders are loaded, validate that the folderId exists in the options
    // If not, set it to '' (Root)
    if (normalizedFolderId && normalizedFolderId !== '') {
      // Use strict comparison to find matching folder
      const folderExists = folderOptions.value.find(opt => {
        // Compare as strings to handle type mismatches
        return String(opt.value) === String(normalizedFolderId)
      })
      
      if (!folderExists) {
        // Folder doesn't exist (might have been deleted), set to Root
        console.warn(`Folder ${normalizedFolderId} not found in options, setting to Root`)
        normalizedFolderId = ''
      }
    }
    
    // Update editForm.folderId with the validated value (always use string)
    editForm.value.folderId = String(normalizedFolderId || '')
    
    await Promise.all([
      loadActivities(),
      loadTestRunHistory(),
      loadLinkedBugs(),
      loadFolderTestCases()
    ])
    
    const testRunIdParam = route.query.testRunId
    if (testRunIdParam) {
      await loadTestRunForNavigation(testRunIdParam)
    }
  } catch (error) {
    console.error('Error loading test case:', error)
    showToast('Error loading test case', 'error')
  } finally {
    loading.value = false
    // Force HLTabs to re-render with updated data
    tabsKey.value++
  }
}

const loadTestRunForNavigation = async (runId) => {
  try {
    testRunId.value = runId
    const testRun = await testRunStore.getTestRun(runId)
    
    if (testRun && testRun.test_cases) {
      const transformedCases = await Promise.all(
        testRun.test_cases.map(async (tc) => {
            let actualTestCase = null
            try {
              if (testCaseService && testCaseService.findBy) {
                const testCases = await testCaseService.findBy('testCaseId', tc.testCaseId)
              if (testCases && testCases.length > 0) actualTestCase = testCases[0]
            }
          } catch (error) {
            console.error('Error fetching test case:', error)
          }
          
            return {
              id: actualTestCase?.id, // Include database id for navigation
              testCaseId: tc.testCaseId,
              name: actualTestCase?.name || `Test Case ${tc.testCaseId}`,
            status: tc.status || 'not_started'
          }
        })
      )
      
      testRunTestCases.value = transformedCases
      if (!currentTestCaseId.value) {
        currentTestCaseId.value = route.params.caseId
      }
    }
  } catch (error) {
    console.error('Error loading test run for navigation:', error)
    testRunId.value = null
    testRunTestCases.value = []
  }
}

const loadFolderTestCases = async () => {
  try {
    if (!route.params.projectId || !testCase.value) return
    
    const allTestCases = await testCaseStore.fetchTestCasesByProject(route.params.projectId)
    const currentFolderId = testCase.value.folderId
    const isRootFolder = !currentFolderId
    
    const folderTestCasesList = allTestCases.filter(tc => {
      if (isRootFolder) return !tc.folderId
        return tc.folderId === currentFolderId
    })
    
    // Sort by creation timestamp in descending order (newest first)
    folderTestCases.value = folderTestCasesList.sort((a, b) => {
      const dateA = a.created_at || a.createdAt
      const dateB = b.created_at || b.createdAt
      let timeA = 0
      let timeB = 0
      
      if (dateA) {
        if (dateA instanceof Date) {
          timeA = dateA.getTime()
        } else if (typeof dateA === 'string') {
          const parsed = new Date(dateA).getTime()
          timeA = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateA === 'number') {
          timeA = dateA
        }
      }
      
      if (dateB) {
        if (dateB instanceof Date) {
          timeB = dateB.getTime()
        } else if (typeof dateB === 'string') {
          const parsed = new Date(dateB).getTime()
          timeB = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateB === 'number') {
          timeB = dateB
        }
      }
      
      return timeB - timeA // Descending order (newest first)
    })
    
    folderCurrentTestCaseId.value = testCase.value.testCaseId || testCase.value.id
  } catch (error) {
    console.error('Error loading folder test cases:', error)
    folderTestCases.value = []
  }
}

const getFolderName = (folderId) => {
  if (!folderId || !folders.value.length) return null
  const findFolder = (folders, id) => {
    for (const folder of folders) {
      if (folder.id === id) return folder
      if (folder.children) {
        const found = findFolder(folder.children, id)
        if (found) return found
      }
    }
    return null
  }
  const folder = findFolder(folders.value, folderId)
  return folder ? folder.name : null
}

const getAssigneeName = (assigneeId) => {
  if (!assigneeId || !users.value.length) return null
  const user = users.value.find(u => u.id === assigneeId)
  return user ? (user.name || user.email) : null
}

const loadTestRunHistory = async () => {
  try {
    const { testRunService } = await import('@/database/services')
    const allTestRuns = await testRunService.getByProject(route.params.projectId)
    
    const testRunsWithThisCase = allTestRuns.filter(testRun => {
      if (!testRun.test_cases || !Array.isArray(testRun.test_cases)) return false
      return testRun.test_cases.some(tc => 
        tc.testCaseId === testCase.value?.id || 
        tc.testCaseId === testCase.value?.testCaseId
      )
    })
    
    testRunHistory.value = await Promise.all(testRunsWithThisCase.map(async (testRun) => {
      const testCaseInRun = testRun.test_cases.find(tc => 
        tc.testCaseId === testCase.value?.id || 
        tc.testCaseId === testCase.value?.testCaseId
      )
      
      let executorName = 'Not executed yet'
      if (testCaseInRun?.executed_by) {
        try {
          let executor = users.value.find(u => u.id === testCaseInRun.executed_by)
          if (!executor) executor = await userStore.getUserById(testCaseInRun.executed_by)
          if (executor) executorName = executor.name || executor.email || 'Unknown'
        } catch (error) {
          executorName = 'Unknown'
        }
      }
      
      return {
        id: testRun.id,
        testRunId: testRun.id,
        testRunName: testRun.name || `Test Run ${testRun.id}`,
        status: testCaseInRun?.status || 'not_started',
        testRunStatus: testRun.status || 'Unknown',
        executedAt: testCaseInRun?.executed_at || testRun.created_at,
        executedBy: executorName,
        notes: testCaseInRun?.notes || ''
      }
    }))
    
    testRunHistory.value.sort((a, b) => new Date(b.executedAt) - new Date(a.executedAt))
  } catch (error) {
    console.error('Error loading test run history:', error)
    testRunHistory.value = []
  }
}

const loadLinkedBugs = async () => {
  loadingLinkedBugs.value = true
  try {
    if (!testCase.value) return
    
    const linkedBugIds = testCase.value.bug_ids || []
    
    if (linkedBugIds.length === 0) {
      linkedBugs.value = []
      await loadAvailableBugs()
      return
    }
    
    const { bugService } = await import('@/database/services')
    const bugPromises = linkedBugIds.map(async (bugId) => {
      try {
        if (bugId.startsWith('B')) {
          const bugs = await bugService.findBy('bugId', bugId)
          if (bugs && bugs.length > 0) return bugs[0]
          }
        const bug = await bugService.getById(bugId)
        return bug
  } catch (error) {
        console.error('❌ Error fetching bug:', bugId, error)
        return null
      }
    })
    
    const bugs = await Promise.all(bugPromises)
    linkedBugs.value = bugs.filter(bug => bug !== null)
    await loadAvailableBugs()
  } catch (error) {
    console.error('Error loading linked bugs:', error)
    linkedBugs.value = []
  } finally {
    loadingLinkedBugs.value = false
    // Force reactivity update
    await nextTick()
  }
}

const loadAvailableBugs = async () => {
  try {
    if (!route.params.projectId) return
    
    const { bugService } = await import('@/database/services')
    const allBugs = await bugService.getByProject(route.params.projectId)
    
    const linkedBugIds = testCase.value?.bug_ids || []
    availableBugs.value = allBugs.filter(bug => !linkedBugIds.includes(bug.id))
  } catch (error) {
    console.error('Error loading available bugs:', error)
    availableBugs.value = []
  }
}

const linkSelectedBug = async () => {
  if (!selectedBugId.value) return
  
  try {
    
    // Update test case with bug
    const bugIds = [...(testCase.value.bug_ids || []), selectedBugId.value]
    
    
    await testCaseStore.updateTestCase(testCase.value.id, {
      bug_ids: bugIds,
      project_id: route.params.projectId,
      updated_by: authStore.user?.id,
      updated_by_name: authStore.user?.name || authStore.user?.email || 'Unknown'
    })
    
    
    // Update the bug to add this test case (bidirectional linking)
    const bug = await bugService.getById(selectedBugId.value)
    if (bug) {
      const updatedTestCaseIds = [...(bug.test_case_ids || []), testCase.value.id]
      await bugService.updateBug(selectedBugId.value, {
        test_case_ids: updatedTestCaseIds,
        updated_by: authStore.user?.id,
        updated_by_name: authStore.user?.name || authStore.user?.email || 'Unknown'
      })
    }
    
    showSuccess('Bug linked successfully')
    selectedBugId.value = null
    bugSearchQuery.value = ''
    await loadTestCase()
    await loadAvailableBugs()
  } catch (error) {
    showError('Failed to link bug')
  }
}

const isBugLinkedOnlyToThisTestCase = (bugId) => {
  const bug = linkedBugs.value.find(b => b.id === bugId)
  if (!bug) return false
  // Check if this bug is linked to only this test case
  const linkedTestCaseCount = bug.test_case_ids?.length || 0
  return linkedTestCaseCount === 1
}

const unlinkBug = async (bugId) => {
  try {
    // Fetch the bug to check if it has other test cases linked
    const bug = await bugService.getById(bugId)
    
    if (!bug) {
      showError('Bug not found')
      return
    }
    
    // Check if removing this test case would leave the bug with no test cases
    const linkedTestCases = bug.test_case_ids || []
    const updatedBugTestCases = linkedTestCases.filter(id => id !== testCase.value.id)
    
    if (updatedBugTestCases.length === 0) {
      showError('Cannot unlink this bug. Test case is a mandatory field for bugs. Please link this bug to another test case first before unlinking.')
      return
    }
    
    // Check if this bug is referenced in any test runs for this test case
    const { testRunService } = await import('@/database/services')
    const allTestRuns = await testRunService.getByProject(route.params.projectId)
    
    for (const testRun of allTestRuns) {
      if (testRun.test_cases && Array.isArray(testRun.test_cases)) {
        const testCaseInRun = testRun.test_cases.find(tc => 
          tc.testCaseId === testCase.value.id || tc.testCaseId === testCase.value?.testCaseId
        )
        
        if (testCaseInRun && testCaseInRun.bugIds && testCaseInRun.bugIds.includes(bugId)) {
          showError(`Cannot unlink this bug. It is referenced in test run "${testRun.name || testRun.id}". Please remove it from the test run first.`)
          return
        }
      }
    }
    
    if (!confirm('Are you sure you want to unlink this bug?')) return
    
    const updatedBugIds = (testCase.value.bug_ids || []).filter(id => id !== bugId)
    
    // Update the test case
    await testCaseStore.updateTestCase(testCase.value.id, {
      bug_ids: updatedBugIds,
      project_id: route.params.projectId,
      updated_by: authStore.user?.id,
      updated_by_name: authStore.user?.name || authStore.user?.email || 'Unknown'
    })
    
    // Update the bug to remove this test case
    await bugService.updateBug(bugId, {
      test_case_ids: updatedBugTestCases,
      updated_by: authStore.user?.id,
      updated_by_name: authStore.user?.name || authStore.user?.email || 'Unknown'
    })
    
    showSuccess('Bug unlinked successfully')
    await loadTestCase()
  } catch (error) {
    console.error('Error unlinking bug:', error)
    showError('Failed to unlink bug')
  }
}

const selectBugToLink = (bugId) => {
  selectedBugId.value = bugId
  showBugDropdown.value = false
  
  // Show the selected bug in the search field
  const selectedBug = availableBugs.value.find(bug => bug.id === bugId)
  if (selectedBug) {
    bugSearchQuery.value = `${selectedBug.bugId} - ${selectedBug.title}`
  }
}

const hideBugDropdown = () => {
  setTimeout(() => {
    showBugDropdown.value = false
  }, 200)
}

const refreshBugs = async () => {
  await loadLinkedBugs()
}

const createNewBug = () => {
  router.push(`/projects/${route.params.projectId}/bugs/create?testCaseId=${route.params.caseId}`)
}

const refreshTestRuns = async () => {
  await loadTestRunHistory()
  showSuccess('Test runs refreshed')
}

const loadActivities = async () => {
  loadingActivity.value = true
  try {
    // Ensure users are loaded before resolving activity user names
    if (!users.value || users.value.length === 0) {
      try {
        users.value = await userStore.getUsers() || []
      } catch (error) {
        console.warn('Error loading users for activities:', error)
      }
    }
    
    let response = []
    try {
      response = await testCaseStore.getTestCaseActivities(route.params.caseId)
    } catch (error) {
      console.warn('Error fetching activities:', error)
    }
    
    if (testCase.value && testCase.value.activities) {
      response = [...(testCase.value.activities || []), ...response]
    }
    
    // Resolve user names for all activities
    const activitiesWithUserNames = await Promise.all(
      (response || []).map(async (activity) => {
        // If activity already has user_name or created_by_name, use it
        if (activity.user_name || activity.created_by_name) {
          activity.user_name = activity.user_name || activity.created_by_name
          return activity
        }
        
        // Try to resolve user name from user_id or created_by
        const userId = activity.user_id || activity.created_by
        if (userId) {
          try {
            // First check in users.value (already loaded)
            let user = users.value.find(u => u.id === userId || u.uid === userId)
            
            // If not found, try to fetch from userStore
            if (!user) {
              try {
                user = await userStore.getUserById(userId)
              } catch (fetchError) {
                console.warn(`Could not fetch user ${userId} from userStore:`, fetchError)
              }
            }
            
            // Set user_name if user found
            if (user) {
              activity.user_name = user.name || user.displayName || user.email || 'Unknown User'
            } else {
              // If user not found, check if it's the current user
              if (authStore.user && (authStore.user.id === userId || authStore.user.uid === userId)) {
                activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
              } else {
                activity.user_name = 'Unknown User'
              }
            }
          } catch (error) {
            console.warn(`Error resolving user for activity ${activity.id}:`, error)
            // Fallback to current user if available
            if (authStore.user) {
              activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
            } else {
              activity.user_name = 'Unknown User'
            }
          }
        } else {
          // No user_id or created_by, check if it's a system activity or use current user
          if (authStore.user) {
            activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
          } else {
            activity.user_name = 'Unknown User'
          }
        }
        
        return activity
      })
    )
    
    // Filter out redundant generic "updated" activities when specific field_updated activities exist
    // Group activities by timestamp (within 1 second) and user
    const filteredActivities = activitiesWithUserNames.filter((activity, index, arr) => {
      // Keep all non-updated activities (created, deleted, restored, etc.)
      if (activity.type !== 'updated') {
        return true
      }
      
      // For generic "updated" activities, check if there are specific field_updated activities
      // at the same time (within 1 second) from the same user
      const activityTime = activity.timestamp ? new Date(activity.timestamp).getTime() : 0
      const activityUserId = activity.user_id || activity.created_by
      
      // Check if there are any field_updated activities at the same time
      const hasSpecificFieldUpdates = arr.some(otherActivity => {
        if (otherActivity === activity) return false
        if (otherActivity.type !== 'field_updated') return false
        
        const otherTime = otherActivity.timestamp ? new Date(otherActivity.timestamp).getTime() : 0
        const otherUserId = otherActivity.user_id || otherActivity.created_by
        const timeDiff = Math.abs(activityTime - otherTime)
        
        // Same user and within 1 second
        return otherUserId === activityUserId && timeDiff < 1000
      })
      
      // Remove generic "updated" activity if specific field updates exist
      return !hasSpecificFieldUpdates
    })
    
    // Sort activities in descending order (newest first)
    const sortedActivities = filteredActivities.sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0)
      const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0)
      return dateB - dateA // Descending order
    })
    
    // Set activities
    activities.value = sortedActivities
  } catch (error) {
    console.error('Error loading activities:', error)
    activities.value = []
  } finally {
    loadingActivity.value = false
  }
}

// Helper functions to format display values
const getTestTypeLabel = (value) => {
  const type = getTestCaseTypeByValue(value)
  return type?.label || value || 'N/A'
}

const getCaseTypeLabel = (value) => {
  const type = getCaseTypeByValue(value)
  return type?.label || value || 'N/A'
}

const getAutomationStatusLabel = (value) => {
  const status = getAutomationStatusByValue(value)
  return status?.label || value || 'N/A'
}

const getStatusLabel = (value) => {
  const status = getTestCaseStatusByValue(value)
  return status?.label || value || 'N/A'
}

const getPriorityLabel = (value) => {
  const priority = getPriorityByValue(value)
  return priority?.label || value || 'N/A'
}

const viewBugInNewTab = (bugId) => {
  window.open(`/projects/${route.params.projectId}/bugs/${bugId}`, '_blank')
}

const viewTestRunInNewTab = (testRunId) => {
  window.open(`/projects/${route.params.projectId}/test-runs/${testRunId}`, '_blank')
}

const getBugStatusVariant = (status) => {
  const variants = {
    'open': 'danger', 'in_progress': 'warning', 'resolved': 'info',
    'closed': 'success', 'blocked': 'secondary', 'reopened': 'danger',
    'not_a_bug': 'info', 'setup_issue': 'warning'
  }
  return variants[status] || 'secondary'
}

const getBugSeverityVariant = (severity) => {
  const variants = {
    'critical': 'danger', 'high': 'warning', 'medium': 'info', 'low': 'secondary'
  }
  return variants[severity] || 'secondary'
}

const getBugPriorityVariant = (priority) => {
  const variants = {
    'critical': 'danger', 'high': 'warning', 'medium': 'info', 'low': 'secondary'
  }
  return variants[priority] || 'secondary'
}

const formatBugStatus = (status) => {
  if (!status) return 'N/A'
  return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

const formatBugSeverity = (severity) => {
  if (!severity) return 'N/A'
  return severity.charAt(0).toUpperCase() + severity.slice(1)
}

const formatBugPriority = (priority) => {
  if (!priority) return 'N/A'
  return priority.charAt(0).toUpperCase() + priority.slice(1)
}

const formatExecutionStatus = (status) => {
  if (!status) return 'N/A'
  return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

const formatEnvironment = (env) => {
  if (!env) return 'N/A'
  return env.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
}

const getEnvironmentVariant = (env) => {
  if (!env) return 'secondary'
  if (env.includes('production') || env.includes('prod')) return 'danger'
  if (env.includes('staging') || env.includes('stage')) return 'warning'
  if (env.includes('qa') || env.includes('testing') || env.includes('test')) return 'info'
  if (env.includes('development') || env.includes('dev')) return 'secondary'
  return 'secondary'
}

const getActivityIconClass = (type) => {
  const classes = {
    'created': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
    'updated': 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
    'field_updated': 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
    'executed': 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400',
    'status_changed': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400',
    'bug_linked': 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400',
    'deleted': 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400',
    'restored': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
    'default': 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }
  return classes[type] || classes.default
}

const goBack = () => {
  // Check if there's a testRunId in the query params (came from test run)
  const testRunId = route.query.testRunId || route.query.runId
  
  if (testRunId) {
    // Navigate back to the test run detail page
    router.push(`/projects/${route.params.projectId}/test-runs/${testRunId}`)
  } else if (window.history.length > 1) {
    // Use browser back if there's history
    router.back()
  } else {
    // Default to test cases list
  router.push(`/projects/${route.params.projectId}/test-cases`)
  }
}

// Test steps functions
const startEditStep = (index, step) => {
  editingStepIndex.value = index
  editStepForm.value = {
    description: typeof step === 'string' ? step : (step.description || step.action || ''),
    expectedResult: typeof step === 'string' ? '' : (step.expectedResult || '')
  }
}

const cancelEditStep = () => {
  editingStepIndex.value = null
  editStepForm.value = { description: '', expectedResult: '' }
}

const saveStep = async (index) => {
  try {
    const steps = Array.isArray(testCase.value.steps) ? [...testCase.value.steps] : []
    steps[index] = {
      description: editStepForm.value.description,
      expectedResult: editStepForm.value.expectedResult
    }
    
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    await testCaseStore.updateTestCase(testCase.value.id, {
      steps,
      project_id: route.params.projectId,
      updated_by: userId,
      updated_by_name: userName
    })
    
    await loadTestCase()
    cancelEditStep()
  } catch (error) {
    console.error('Update step error:', error)
    showError('Failed to update test step')
  }
}

const deleteStep = async (index) => {
  if (!confirm('Are you sure you want to delete this test step?')) return
  
  try {
    const steps = Array.isArray(testCase.value.steps) ? [...testCase.value.steps] : []
    steps.splice(index, 1)
    
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    await testCaseStore.updateTestCase(testCase.value.id, {
      steps,
      project_id: route.params.projectId,
      updated_by: userId,
      updated_by_name: userName
    })
    
    showSuccess('Test step deleted successfully')
    await loadTestCase()
    } catch (error) {
    console.error('Delete step error:', error)
    showError('Failed to delete test step')
  }
}

const addStep = async () => {
  try {
    if (!newStepForm.value.description.trim()) {
      showError('Test step description is required')
      return
    }
    
    const steps = Array.isArray(testCase.value.steps) ? [...testCase.value.steps] : []
    steps.push({
      description: newStepForm.value.description,
      expectedResult: newStepForm.value.expectedResult
    })
    
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    await testCaseStore.updateTestCase(testCase.value.id, {
      steps,
      project_id: route.params.projectId,
      updated_by: userId,
      updated_by_name: userName
    })
    
    showSuccess('Test step added successfully')
    newStepForm.value = { description: '', expectedResult: '' }
    showAddStep.value = false
    await loadTestCase()
    } catch (error) {
    console.error('Add step error:', error)
    showError('Failed to add test step')
  }
}

const cancelAddStep = () => {
  showAddStep.value = false
  newStepForm.value = { description: '', expectedResult: '' }
}

// API config functions
const startEditApiField = (field, currentValue) => {
  if (!can.editTestCases()) return
  editingApiField.value = field
  apiEditForm.value[field] = currentValue || ''
}

const cancelEditApiField = () => {
  editingApiField.value = null
}

const saveApiField = async (field) => {
  try {
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    const updatedApiConfig = {
      ...(testCase.value.apiConfig || {}),
      [field]: apiEditForm.value[field]
    }
    
    await testCaseStore.updateTestCase(testCase.value.id, {
      apiConfig: updatedApiConfig,
      project_id: route.params.projectId,
      updated_by: userId,
      updated_by_name: userName
    })
    
    await loadTestCase()
    cancelEditApiField()
  } catch (error) {
    console.error('Update API config error:', error)
    showError('Failed to update API configuration')
  }
}

// Inline editing functions
const startEdit = (field, currentValue) => {
  if (!can.editTestCases()) return
  editingField.value = field
  editForm.value[field] = currentValue
  titleError.value = false
  
  nextTick(() => {
    if (field === 'name') {
      if (nameInput.value) {
        nameInput.value.focus()
        nameInput.value.select()
      }
    } else if (field === 'status' || field === 'priority' || field === 'type' || field === 'case_type' || field === 'automation_status' || field === 'assigned_to' || field === 'folderId') {
      const selectEl = document.querySelector('select')
      if (selectEl) {
        selectEl.focus()
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            })
            selectEl.dispatchEvent(clickEvent)
          })
        })
      }
    } else if (field === 'estimated_time') {
      const inputEl = document.querySelector('input[type="number"]')
      if (inputEl) {
        inputEl.focus()
        inputEl.select()
      }
    } else if (field === 'description') {
      if (descriptionInput.value) {
        descriptionInput.value.focus()
        const textLength = descriptionInput.value.value.length
        descriptionInput.value.setSelectionRange(textLength, textLength)
      }
    } else if (field === 'prerequisites') {
      if (prerequisitesInput.value) {
        prerequisitesInput.value.focus()
        const textLength = prerequisitesInput.value.value.length
        prerequisitesInput.value.setSelectionRange(textLength, textLength)
      }
    } else if (field === 'testData') {
      if (testDataInput.value) {
        testDataInput.value.focus()
        const textLength = testDataInput.value.value.length
        testDataInput.value.setSelectionRange(textLength, textLength)
      }
    }
  })
}

const cancelEdit = () => {
  editingField.value = null
  titleError.value = false
}

// Special handler for folder changes to prevent watcher interference
const handleFolderChange = async (value) => {
  if (!can.editTestCases()) return
  
  // Disable watcher and set flags IMMEDIATELY to prevent watcher from interfering
  folderIdWatcherEnabled.value = false
  isUserSelecting = true
  isUpdatingFolderId = true
  
  // Update editForm immediately to reflect user's selection
  editForm.value.folderId = value
  
  // Re-enable watcher after a brief moment to allow v-model to update
  nextTick(() => {
    folderIdWatcherEnabled.value = true
  })
  
  // Now call the regular handler
  await handleFieldChange('folderId', value)
}

const handleFieldChange = async (field, value) => {
  if (!can.editTestCases()) return
  
  try {
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    // Normalize folderId: empty string, null, or undefined means "Root" (no folder)
    let normalizedValue = value
    if (field === 'folderId') {
      // Convert null/undefined/empty to empty string for "Root"
      if (!value || String(value).trim() === '') {
        normalizedValue = ''
      } else {
        normalizedValue = String(value).trim()
      }
      
      // Set flags to prevent watcher from interfering with user selection
      isUserSelecting = true
      isUpdatingFolderId = true
      
      // Update editForm immediately to reflect user's selection
      editForm.value.folderId = normalizedValue
    }
    
    const updates = { 
      [field]: normalizedValue,
      project_id: route.params.projectId,
      updated_by: userId,
      updated_by_name: userName
    }
    
    const updatedTestCase = await testCaseStore.updateTestCase(testCase.value.id, updates)
    
    // For folderId updates, preserve the value and update local state directly
    // instead of reloading which might reset it
    if (field === 'folderId') {
      // Update testCase with the returned value
      if (updatedTestCase) {
        testCase.value = { ...testCase.value, ...updatedTestCase }
      }
      
      // Ensure editForm.folderId is set to the normalized value
      editForm.value.folderId = normalizedValue
      
      // Reload activities to show the new activity entry
      await loadActivities()
      
      // Reset flags after a brief delay
      setTimeout(() => {
        isUserSelecting = false
        isUpdatingFolderId = false
      }, 100)
    } else {
      // For other fields, reload the test case normally
    await loadTestCase()
    }
    
    showSuccess(`${field} updated successfully`)
  } catch (error) {
    console.error(`Update ${field} error:`, error)
    showError(`Failed to update ${field}`)
    // Reset flags on error
    if (field === 'folderId') {
      isUserSelecting = false
      isUpdatingFolderId = false
    }
  }
}

const saveEdit = async (field) => {
  try {
    // Validate title is not empty
    if (field === 'name') {
      const trimmedName = editForm.value[field]?.trim()
      if (!trimmedName) {
        titleError.value = true
        showError('Test case title cannot be empty')
        return
      }
    }
    
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    const updates = { 
      [field]: editForm.value[field],
      project_id: route.params.projectId,
      updated_by: userId,
      updated_by_name: userName
    }
    
    await testCaseStore.updateTestCase(testCase.value.id, updates)
    await loadTestCase()
    cancelEdit()
  } catch (error) {
    console.error('Update error:', error)
    showError('Failed to update test case')
    cancelEdit()
  }
}

// Computed property for folder tree (flat list for dropdown)
const folderList = computed(() => {
  const flattenFolders = (folders, level = 0) => {
    let result = []
    folders.forEach(folder => {
      result.push({
        ...folder,
        level,
        displayName: '  '.repeat(level) + folder.name
      })
      if (folder.children && folder.children.length > 0) {
        result = result.concat(flattenFolders(folder.children, level + 1))
      }
    })
    return result
  }
  return flattenFolders(folders.value)
})

const loadProjects = async () => {
  try {
    projects.value = await projectStore.getProjects() || []
  } catch (error) {
    console.error('Error loading projects:', error)
  }
}

const loadUsers = async () => {
  try {
    users.value = await userStore.getUsers() || []
  } catch (error) {
    console.error('Error loading users:', error)
  }
}

const loadFolders = async () => {
  try {
    if (route.params.projectId) {
      const { folderService } = await import('@/database/services')
      folders.value = await folderService.getByProject(route.params.projectId) || []
    }
  } catch (error) {
    console.error('Error loading folders:', error)
  }
}

// Watch for folderId changes to ensure it matches an option (only when folders are loaded)
// Only watch when folders are loaded to avoid interfering with user selection
let isUpdatingFolderId = false
let isUserSelecting = false // Track when user is actively selecting
const folderIdWatcherEnabled = ref(true) // Control watcher execution

watch([() => editForm.value.folderId, () => folderOptions.value], ([newFolderId, options]) => {
  // Skip if watcher is disabled or we're programmatically updating or user is selecting
  if (!folderIdWatcherEnabled.value || isUpdatingFolderId || isUserSelecting) return
  
  // Only validate if folders have been loaded
  if (!options || options.length === 0) return
  
  // Normalize null/undefined to empty string
  if (newFolderId === null || newFolderId === undefined) {
    isUpdatingFolderId = true
    nextTick(() => {
      editForm.value.folderId = ''
      isUpdatingFolderId = false
    })
    return
  }
  
  // Check if folderId exists in options (compare as strings to handle type mismatches)
  // Only validate if the value doesn't match any option (don't interfere with valid selections)
  if (newFolderId && String(newFolderId).trim() !== '') {
    const folderExists = options.find(opt => String(opt.value) === String(newFolderId))
    if (!folderExists) {
      // Folder doesn't exist in options (might have been deleted), set to Root
      // Use nextTick to avoid infinite loop
      isUpdatingFolderId = true
      nextTick(() => {
        editForm.value.folderId = ''
        isUpdatingFolderId = false
      })
    }
  }
}, { immediate: false })

watch(() => route.params.caseId, (newCaseId, oldCaseId) => {
  if (newCaseId && newCaseId !== oldCaseId) {
    currentTestCaseId.value = newCaseId
    folderCurrentTestCaseId.value = newCaseId
    loadTestCase()
  }
})

// Watch for additionalTabs changes to debug tab count
watch(() => additionalTabs.value, (newTabs) => {
  const bugsTab = newTabs.find(t => t.id === 'bugs')
  // Only log if there's actual data (skip the initial render before bugs are loaded)
  if (linkedBugs.value.length > 0 || testCase.value?.bug_ids?.length) {
  }
}, { deep: true })

onMounted(() => {
  loadTestCase()
  loadProjects()
  loadUsers()
  loadFolders()
})
</script>

<style scoped>
/* Increase row height for test steps table */
:deep(.test-steps-data-table tr td) {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  vertical-align: top;
  max-width: 100%;
  overflow: hidden;
}

:deep(.test-steps-data-table tr) {
  min-height: 60px;
}

/* Prevent textareas from overflowing table cells */
:deep(.test-steps-data-table table) {
  table-layout: fixed;
  width: 100%;
}

:deep(.test-steps-data-table tr td > div) {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

:deep(.test-steps-data-table tr td) {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

:deep(.test-steps-data-table tr td textarea),
:deep(.test-steps-data-table tr td .hl-input),
:deep(.test-steps-data-table tr td .hl-input textarea),
:deep(.test-steps-data-table tr td input) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  min-width: 0 !important;
}

/* Hide scrollbar by default, show only on hover */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.overflow-y-auto:hover {
  scrollbar-color: rgb(209 213 219) transparent;
}

.dark .overflow-y-auto:hover {
  scrollbar-color: rgb(75 85 99) transparent;
}

/* WebKit browsers */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
}

.overflow-y-auto:hover::-webkit-scrollbar-thumb {
  background: rgb(209 213 219);
}

.dark .overflow-y-auto:hover::-webkit-scrollbar-thumb {
  background: rgb(75 85 99);
}

/* Height constraints for text sections */
.max-h-40 {
  max-height: 10rem; /* 10 rows approximately */
}

.h-12 {
  height: 3rem; /* 3 rows approximately */
}

/* Fix HLInput width - ensure it takes full available space */
:deep(.hl-input),
:deep(.hl-input-wrapper),
:deep(.hr-input),
:deep(.hr-input-wrapper),
:deep(.hl-input > *),
:deep(.hr-input > *) {
  width: 100% !important;
  min-width: 0 !important;
  flex: 1 1 0% !important;
}

/* Specifically target the input element inside HLInput */
:deep(.hl-input input),
:deep(.hr-input input),
:deep(.hl-input-wrapper input),
:deep(.hr-input-wrapper input) {
  width: 100% !important;
  min-width: 0 !important;
}

/* Ensure the container div allows flex growth - target the specific filter controls */
.mb-4.flex.items-center.space-x-3 > :deep(.hl-input),
.mb-4.flex.items-center.space-x-3 > :deep(.hr-input) {
  flex: 1 1 0% !important;
  min-width: 200px !important;
  width: auto !important;
  max-width: 100% !important;
}

/* Fix input field borders - ensure HLInputNumber and HLSelect have visible borders */
:deep(.hl-input-number),
:deep(.hl-input-number input),
:deep(.hl-input-number .hl-input-number__input),
:deep(.hl-select),
:deep(.hl-select .hl-select__trigger),
:deep(.hl-select .hr-base-selection-input) {
  border: 1px solid rgb(209 213 219) !important;
  border-radius: 0.375rem !important;
}

:deep(.dark .hl-input-number),
:deep(.dark .hl-input-number input),
:deep(.dark .hl-input-number .hl-input-number__input),
:deep(.dark .hl-select),
:deep(.dark .hl-select .hl-select__trigger),
:deep(.dark .hl-select .hr-base-selection-input) {
  border: 1px solid rgb(75 85 99) !important;
}

/* Ensure focus states have visible borders */
:deep(.hl-input-number:focus-within),
:deep(.hl-input-number input:focus),
:deep(.hl-select:focus-within),
:deep(.hl-select .hl-select__trigger:focus) {
  border-color: rgb(99 102 241) !important;
  outline: 2px solid rgb(99 102 241 / 0.2) !important;
  outline-offset: 2px !important;
}

/* Ensure dropdown arrow is visible and not cut off */
:deep(.hl-select .hl-select__trigger),
:deep(.hl-select .hr-base-selection-input) {
  padding-right: 2rem !important;
  overflow: visible !important;
}

:deep(.hl-select .hl-select__arrow),
:deep(.hl-select .hr-base-selection-arrow) {
  right: 0.5rem !important;
  pointer-events: none !important;
}
</style>
